/**
 * @fileoverview Disposition Management API Module
 * This module provides API functions for managing member dispositions in the outreach system.
 * Dispositions represent the outcomes or results of member interactions, such as call results,
 * contact attempts, or resolution statuses.
 */

import api from "./api";

/**
 * Adds a new disposition record for a specific member
 * @async
 * @function addDisposition
 * @param {string} memberId - The unique identifier of the member to add disposition for
 * @param {Object} payload - The disposition data to be added
 * @param {string} payload.dispositionType - Type of disposition (e.g., "CALL_COMPLETED", "NO_ANSWER", "RESOLVED")
 * @param {string} [payload.notes] - Additional notes or comments about the disposition
 * @param {string} [payload.outcome] - The outcome or result of the interaction
 * @param {string} [payload.contactMethod] - Method used for contact (phone, email, etc.)
 * @param {string} [payload.followUpRequired] - Whether follow-up action is needed
 * @param {string} [payload.dispositionDate] - Date when the disposition occurred (ISO format)
 * @param {string} [payload.userId] - ID of the user who created the disposition
 * @returns {Promise<Object>} Promise resolving to the created disposition record with:
 *   - dispositionId: Unique identifier for the created disposition
 *   - memberId: Member ID the disposition belongs to
 *   - createdAt: Timestamp when disposition was created
 *   - updatedAt: Timestamp when disposition was last updated
 *   - Additional disposition details as submitted
 * @throws {Error} Throws error if API request fails, member not found, or payload validation fails
 * @example
 * // Add a successful call disposition
 * const disposition = await addDisposition("12345", {
 *   dispositionType: "CALL_COMPLETED",
 *   outcome: "MEMBER_CONTACTED",
 *   notes: "Member agreed to schedule appointment",
 *   contactMethod: "phone",
 *   followUpRequired: "true",
 *   dispositionDate: "2025-10-29T10:30:00Z"
 * });
 *
 * // Add a no-answer disposition
 * const noAnswerDisposition = await addDisposition("12345", {
 *   dispositionType: "NO_ANSWER",
 *   outcome: "CONTACT_ATTEMPT",
 *   notes: "Left voicemail message",
 *   contactMethod: "phone",
 *   followUpRequired: "true"
 * });
 */
export const addDisposition = async (memberId, payload) => {
  try {
    const response = await api.post(
      `/outreach/members/${memberId}/dispositions`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    );

    return response;
  } catch (e) {
    return `addDisposition failed: ${e.message}`;
  }
};

/**
 * Retrieves the disposition history for a specific member
 * @async
 * @function getDispositionHistory
 * @param {string} memberId - The unique identifier of the member to retrieve disposition history for
 * @param {Object} [queryParams] - Optional query parameters
 * @param {number} [queryParams.programId] - Filter by program ID
 * @param {number} [queryParams.drugId] - Filter by drug ID
 * @returns {Promise<Object>} Promise resolving to the disposition history data with:
 * @throws {Error} Throws error if API request fails or member not found
 
 */
export const getMemberDispositions = async (memberId, queryParams = {}) => {
  try {
    const response = await api.get(
      `/outreach/members/${memberId}/dispositions/history`,
      {
        params: queryParams,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
    );

    return response.data;
  } catch (e) {
    console.error(`getDispositionHistory failed: ${e.message}`);
  }
};
